// Shared helpers for the one-way "product includes other products" feature.
//
// The catalog carries `includes` as flat rows:
//   { product_id, included_product_id, included_option_id, display_order }
// A NULL included_option_id means the included product is captured with no
// specific option; otherwise there is one row per captured option. These
// helpers reshape those rows for the admin editor and the live intake form.

// Group flat include rows by parent product, then by included product, in the
// rows' existing (display_order) order. Returns
//   Map<parentProductId, [{ includedProductId, optionIds: number[] }]>
export const groupIncludesByParent = (rows = []) => {
  const byParent = new Map();
  rows.forEach((row) => {
    if (!byParent.has(row.product_id)) byParent.set(row.product_id, new Map());
    const children = byParent.get(row.product_id);
    if (!children.has(row.included_product_id)) {
      children.set(row.included_product_id, {
        includedProductId: row.included_product_id,
        optionIds: [],
      });
    }
    if (row.included_option_id != null) {
      children.get(row.included_product_id).optionIds.push(row.included_option_id);
    }
  });
  const out = new Map();
  byParent.forEach((children, parentId) => out.set(parentId, [...children.values()]));
  return out;
};

// The editor draft shape for one product: [{ included_product_id, option_ids }].
export const includeDraftEntries = (rows, productId) => {
  const children = new Map();
  (rows ?? [])
    .filter((r) => r.product_id === productId)
    .forEach((row) => {
      if (!children.has(row.included_product_id)) {
        children.set(row.included_product_id, {
          included_product_id: row.included_product_id,
          option_ids: [],
        });
      }
      if (row.included_option_id != null) {
        children.get(row.included_product_id).option_ids.push(row.included_option_id);
      }
    });
  return [...children.values()];
};

// Given the grouped includes and the set of currently user-selected product ids,
// compute which products are auto-included and by whom. Returns
//   Map<includedProductId, { parentIds: number[], optionIds: Set<number> }>
// Non-transitive: only direct includes of selected products are considered.
export const computeIncludedProducts = (groupedIncludes, selectedProductIds = []) => {
  const result = new Map();
  selectedProductIds.forEach((parentId) => {
    const children = groupedIncludes.get(parentId);
    if (!children) return;
    children.forEach((child) => {
      if (!result.has(child.includedProductId)) {
        result.set(child.includedProductId, { parentIds: [], optionIds: new Set() });
      }
      const entry = result.get(child.includedProductId);
      if (!entry.parentIds.includes(parentId)) entry.parentIds.push(parentId);
      child.optionIds.forEach((oid) => entry.optionIds.add(oid));
    });
  });
  return result;
};
